import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { AnalysisProvider } from './contexts/AnalysisContext';
import { ScanProvider } from './contexts/ScanContext';
import ErrorBoundary from './components/ErrorBoundary';
import ScanProgressWidget from './components/ScanProgressWidget';
import './index.css';
import App from './App';
import { captureFirstTouch } from './services/analyticsService';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

// Strip trailing /api so relative paths like /api/foo resolve correctly in production
axios.defaults.baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:3002/api').replace(/\/api\/?$/, '');
// Session auth now lives in an httpOnly cookie instead of a manually-attached
// Authorization header — this makes axios send/receive it on every request.
axios.defaults.withCredentials = true;

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV || 'development',
    tracesSampleRate: 0.2,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

captureFirstTouch();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <AuthProvider>
              <AnalysisProvider>
                <ScanProvider>
                  <App />
                  <ScanProgressWidget />
                </ScanProvider>
              </AnalysisProvider>
            </AuthProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </HelmetProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
